import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Logo from '../components/Logo';
import loginFiber from '../assets/images/login-fiber.jpg';
import './Login.css';
import './Signup.css';

const Signup = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    fullName: '',
    inviteCode: '',
  });
  const [hasInviteCode, setHasInviteCode] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.password || !formData.confirmPassword || !formData.fullName) {
      setError(t('signup.errors.fillAll'));
      return;
    }

    if (!hasInviteCode && !formData.companyName) {
      setError(t('signup.errors.companyRequired'));
      return;
    }

    if (hasInviteCode && !formData.inviteCode) {
      setError(t('signup.errors.inviteRequired'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('signup.errors.passwordMismatch'));
      return;
    }

    if (formData.password.length < 8) {
      setError(t('signup.errors.passwordLength'));
      return;
    }

    setLoading(true);

    try {
      const signupData = {
        email: formData.email,
        username: formData.fullName,
        password: formData.password,
        company_name: hasInviteCode ? '' : formData.companyName,
        invite_code: hasInviteCode ? formData.inviteCode : '',
      };

      const result = await signup(signupData);

      if (result.success) {
        navigate('/verify-email', { state: { email: formData.email } });
      } else {
        setError(result.error || t('signup.errors.failed'));
      }
    } catch (err) {
      setError(err.message || t('signup.errors.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="arc-login arc-signup">
      <aside className="arc-login-visual">
        <img src={loginFiber} alt="" className="arc-login-media" />
        <div className="arc-login-veil" aria-hidden="true" />
        <div className="arc-login-visual-copy">
          <button type="button" className="arc-login-brand" onClick={() => navigate('/')}>
            <Logo size="large" variant="full" className="arc-logo-on-dark" />
          </button>
          <h1>{t('signup.branding.title')}</h1>
          <p>{t('signup.branding.description')}</p>
          <ul className="arc-signup-perks">
            <li>{t('signup.branding.feature1')}</li>
            <li>{t('signup.branding.feature2')}</li>
            <li>{t('signup.branding.feature3')}</li>
          </ul>
        </div>
      </aside>

      <main className="arc-login-panel">
        <div className="arc-login-toolbar">
          <button type="button" className="arc-login-home" onClick={() => navigate('/')}>
            ArcVPN
          </button>
          <LanguageSwitcher variant="segmented" />
        </div>

        <div className="arc-login-card arc-signup-card">
          <header className="arc-login-header">
            <h2>{t('signup.title')}</h2>
            <p>{t('signup.subtitle')}</p>
          </header>

          <form onSubmit={handleSubmit} className="arc-login-form">
            {error && <div className="arc-alert arc-alert-error">{error}</div>}

            <label className="arc-field">
              <span>{t('signup.fullName')}</span>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder={t('signup.fullNamePlaceholder')}
                disabled={loading}
                autoComplete="name"
              />
            </label>

            <label className="arc-field">
              <span>{t('signup.email')}</span>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('signup.emailPlaceholder')}
                disabled={loading}
                autoComplete="email"
              />
            </label>

            <div className="arc-signup-invite">
              <label className="arc-signup-toggle">
                <input
                  type="checkbox"
                  checked={hasInviteCode}
                  onChange={(e) => setHasInviteCode(e.target.checked)}
                />
                <span>{t('signup.inviteCodeToggle')}</span>
              </label>
            </div>

            {!hasInviteCode ? (
              <label className="arc-field">
                <span>{t('signup.companyName')}</span>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder={t('signup.companyNamePlaceholder')}
                  disabled={loading}
                />
              </label>
            ) : (
              <label className="arc-field">
                <span>{t('signup.inviteCode')}</span>
                <input
                  type="text"
                  name="inviteCode"
                  value={formData.inviteCode}
                  onChange={handleChange}
                  placeholder={t('signup.inviteCodePlaceholder')}
                  disabled={loading}
                />
              </label>
            )}

            <label className="arc-field">
              <span>{t('signup.password')}</span>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('signup.passwordPlaceholder')}
                disabled={loading}
                autoComplete="new-password"
              />
            </label>

            <label className="arc-field">
              <span>{t('signup.confirmPassword')}</span>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t('signup.confirmPasswordPlaceholder')}
                disabled={loading}
                autoComplete="new-password"
              />
            </label>

            <button type="submit" className="arc-login-submit" disabled={loading}>
              {loading ? t('signup.loading') : t('signup.signupButton')}
            </button>
          </form>

          <p className="arc-login-footer">
            {t('signup.hasAccount')}{' '}
            <button type="button" className="arc-text-btn" onClick={() => navigate('/login')}>
              {t('signup.loginLink')}
            </button>
          </p>
        </div>
      </main>
    </div>
  );
};

export default Signup;
